import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, ArrowUp, ArrowDown, ArrowRight, ArrowLeft as ArrowL, Home, RotateCcw, Trophy, Medal, HelpCircle } from "lucide-react";
import api from "../../lib/api";
import { ThemedShell, ThemedCard } from "../../components/child/ThemedShell";
import { Button } from "../../components/ui/button";
import { sfx, music } from "../../lib/sound";

const A = {
  bgAlley: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/4fa46dc3b35ce83cc983e6bb4bb3d6285dbc064c603ce23ec92f2f45ed83aea5.png",
  bgHouse: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/c66e7cfaf04710ecbf76bcb85d1f77175e1b50cda434f87a4a6990aedff0c0f1.png",
  catJump: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/5d849adcc27c59c172699e92aa17e1a886079fd7b51091e572bcd10fa97b50e9.png",
  catScared: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/97e9511b42b3fe0341420fb709b4a7a78bfe6631ffcc43440f43e53ce5f30afb.png",
  bulldogSleep: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/52c0a8a77ec08af24f94ac811fd7a688576bd83c3661c8156b06390049c0ba37.png",
  bulldogAngry: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/60e728a44e70808dca26f69a6bcd07692afae946880abe7ee281d2464e16272e.png",
  kibble: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/20b898733931f3b0a143b7f638bdf6e58d6ee2a658b12cc5a49831c4e8ca92da.png",
  fart: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/9619957608b907ae40f6448d856bd2547ac8a55b7ba3f6a6336983f9b2c5f547.png",
  window: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/5ed377224cd5529449df6a8e0a9b1abf5ef601edd4088e39b9a3d1ad18745621.png",
  logo: "https://static.prod-images.emergentagent.com/jobs/d586efdf-ef9d-49f8-8917-3ab0a3e986a3/images/7c346d3bc64ee10e7c0bdb7844efff69801b5806fbd797ea6cb6261a81a6b509.png",
};

const START_LIVES = 3, FLOORS = 8, GRAV = 1750, JUMP_V = -720, MOVE = 285, FART_V = -560, FART_MAX = 3, FART_REGEN = 0.7;

export default function AlleyCat() {
  const navigate = useNavigate();
  const [phase, setPhase] = useState("menu");
  const [hud, setHud] = useState({ stage: 1, info1: "", lives: START_LIVES, score: 0, fart: FART_MAX });
  const [result, setResult] = useState(null);
  const [board, setBoard] = useState(null);
  const [banner, setBanner] = useState(null);
  const [showHelp, setShowHelp] = useState(() => localStorage.getItem("la_cfa_help") !== "seen");
  const closeHelp = () => { setShowHelp(false); localStorage.setItem("la_cfa_help", "seen"); };

  const canvasRef = useRef(null);
  const dim = useRef({ W: 400, H: 700, dpr: 1 });
  const G = useRef(null);
  const rafRef = useRef(0);
  const keys = useRef({ l: false, r: false, u: false, d: false });
  const scoreRef = useRef(0);
  const imgs = useRef({});

  useEffect(() => {
    Object.entries(A).forEach(([k, url]) => { const im = new Image(); im.crossOrigin = "anonymous"; im.src = url; imgs.current[k] = im; });
  }, []);

  const size = () => {
    const cv = canvasRef.current; if (!cv) return;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const W = cv.clientWidth, H = cv.clientHeight;
    cv.width = Math.round(W * dpr); cv.height = Math.round(H * dpr);
    dim.current = { W, H, dpr };
  };

  // ================= build stages =================
  const buildClimb = (lives, score, diff) => {
    const { W, H } = dim.current;
    const floorGap = Math.max(105, H * 0.155);
    const plats = [{ y: 0, x: W / 2, w: W * 3, type: "ground" }];
    // starting bins/fence step
    plats.push({ y: -floorGap * 0.55, x: W * 0.22, w: 90, type: "bin" });
    for (let i = 1; i <= FLOORS; i++) plats.push({ y: -i * floorGap - floorGap * 0.55, x: W / 2, w: W * 0.62, type: "line", floor: i, amp: (18 + i * 5) * (1 + diff * 0.08), spd: 0.5 + i * 0.04 + diff * 0.03, ph: Math.random() * 6 });
    const winY = -FLOORS * floorGap - floorGap * 0.55 - 62;
    const cat = { x: W * 0.22, y: -floorGap * 0.55, vx: 0, vy: 0, onPlat: plats[1], face: 1, floor: 0, jumps: 1 };
    return {
      stage: 1, plats, winY, floorGap, cat, lives, score, diff,
      fart: FART_MAX, farts: [], t: 0, dead: false, grace: 1.2, climbing: false,
      camY: -H * 0.6, worldTop: winY - H * 0.28, worldBot: H * 0.42,
    };
  };

  const buildHouse = (lives, score, diff) => {
    const { W, H } = dim.current;
    const cat = { x: W * 0.5, y: H * 0.78, face: 1 };
    const total = 12 + diff * 2;
    const kibble = [];
    for (let i = 0; i < total; i++) kibble.push({ x: 30 + Math.random() * (W - 60), y: H * (0.5 + Math.random() * 0.4), got: false });
    const timeLeft = Math.max(22, 42 - diff * 4);
    const dog = { x: W * 0.16, y: H * 0.62, wake: 0, state: "sleep", stun: 0 };
    return { stage: 2, cat, lives, score, diff, kibble, eaten: 0, total, timeLeft, dog, fart: FART_MAX, farts: [], t: 0, dead: false, grace: 0.8 };
  };

  const startGame = () => { G.current = buildClimb(START_LIVES, 0, 0); scoreRef.current = 0; setResult(null); setBanner(null); setPhase("play"); };

  // ================= input =================
  const doJump = () => {
    const g = G.current; if (!g || g.dead || g.stage !== 1) return;
    if (g.cat.jumps > 0) { g.cat.vy = JUMP_V; g.cat.onPlat = null; g.cat.jumps--; sfx.click && sfx.click(); }
  };
  const doFart = () => {
    const g = G.current; if (!g || g.dead) return;
    if (g.fart < 1) return;
    g.fart -= 1;
    if (g.stage === 1) { g.cat.vy = FART_V; g.cat.onPlat = null; }
    else { g.dog.stun = 1.6; g.dog.wake = Math.max(0, g.dog.wake - 60); g.dog.state = "sleep"; }
    g.farts.push({ x: g.cat.x, y: g.cat.y + (g.stage === 1 ? 18 : 14), t: 0 });
    sfx.pop ? sfx.pop() : (sfx.click && sfx.click());
  };
  useEffect(() => {
    const kd = (e) => {
      const m = { ArrowLeft: "l", ArrowRight: "r", ArrowUp: "u", ArrowDown: "d" }[e.key]; if (m) { e.preventDefault(); keys.current[m] = true; if (m === "u") doJump(); }
      if (e.key === " ") { e.preventDefault(); doJump(); }
      if (e.key === "f" || e.key === "F" || e.key === "Shift") { e.preventDefault(); doFart(); }
    };
    const ku = (e) => { const m = { ArrowLeft: "l", ArrowRight: "r", ArrowUp: "u", ArrowDown: "d" }[e.key]; if (m) keys.current[m] = false; };
    window.addEventListener("keydown", kd); window.addEventListener("keyup", ku);
    return () => { window.removeEventListener("keydown", kd); window.removeEventListener("keyup", ku); };
  }, []);

  useEffect(() => {
    if (process.env.NODE_ENV === "production") return;
    window.__cfaTest = {
      house: () => { const g = G.current; if (g) G.current = buildHouse(g.lives, g.score, g.diff); },
      clearHouse: () => { const g = G.current; if (g && g.stage === 2) winHouse(g); },
      winClimb: () => { const g = G.current; if (g && g.stage === 1) winClimb(g); },
    };
    return () => { delete window.__cfaTest; };
  }, []);

  useEffect(() => {
    if (phase !== "play") return;
    size(); if (!G.current) G.current = buildClimb(START_LIVES, 0, 0); music.start();
    let last = 0;
    const loop = (ts) => {
      if (!last) last = ts; let dt = (ts - last) / 1000; last = ts; if (dt > 0.05) dt = 0.05;
      const g = G.current;
      if (g && !g.dead) { if (g.stage === 1) updateClimb(g, dt); else updateHouse(g, dt); }
      render();
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => { cancelAnimationFrame(rafRef.current); music.stop(); };
  }, [phase]);

  // ================= transitions =================
  const loseLife = (g) => {
    g.lives--; sfx.wrong && sfx.wrong();
    if (g.lives < 0) { endGame(g); return; }
    g.grace = 1.3; g.fart = FART_MAX;
    if (g.stage === 1) { const c = g.cat; c.x = dim.current.W * 0.22; c.y = -g.floorGap * 0.55; c.vy = 0; c.onPlat = g.plats[1]; c.floor = 0; c.jumps = 1; g.climbing = false; }
    else { g.cat.x = dim.current.W * 0.5; g.cat.y = dim.current.H * 0.78; g.dog.state = "sleep"; g.dog.wake = 0; g.dog.stun = 0; }
    setHudFromGame(g);
  };
  const winClimb = (g) => {
    g.dead = true; g.score += 500; scoreRef.current = g.score; sfx.win && sfx.win();
    setBanner({ title: "Binnen! 🪟", sub: "+500 — nu de brokjes! Wees snel! 🐾" });
    setTimeout(() => { const ng = buildHouse(g.lives, g.score, g.diff); G.current = ng; setBanner(null); }, 1600);
  };
  const winHouse = (g) => {
    g.dead = true; g.score += 800; g.lives++; scoreRef.current = g.score; sfx.win && sfx.win();
    setBanner({ title: "Alle brokjes op! 😻", sub: "+800 & +1 leven — volgend level!" });
    setTimeout(() => { const ng = buildClimb(g.lives, g.score, g.diff + 1); G.current = ng; setBanner(null); }, 1700);
  };
  const setHudFromGame = (g) => {
    const info1 = g.stage === 1 ? `Verd. ${g.cat.floor}/${FLOORS}` : `🍖 ${g.eaten}/${g.total}  ⏱ ${Math.ceil(g.timeLeft)}`;
    setHud({ stage: g.stage, info1, lives: g.lives, score: g.score, fart: Math.floor(g.fart) });
  };

  const endGame = async (g) => {
    g.dead = true; cancelAnimationFrame(rafRef.current); music.stop(); setPhase("over");
    const floor = Math.max(g.cat?.floor || 0, (g.diff || 0) * FLOORS + (g.stage === 2 ? FLOORS : 0));
    try { const { data } = await api.post("/child/alley/score", { score: g.score, floor, won: false }); setResult({ ...data, score: g.score, floor }); }
    catch { setResult({ score: g.score, floor }); }
  };

  // ================= updates =================
  const stepFarts = (g, dt) => { for (const f of g.farts) f.t += dt; g.farts = g.farts.filter((f) => f.t < 0.6); if (g.fart < FART_MAX) g.fart = Math.min(FART_MAX, g.fart + FART_REGEN * dt); };

  const updateClimb = (g, dt) => {
    g.t += dt; const c = g.cat; const { W, H } = dim.current; if (g.grace > 0) g.grace -= dt; stepFarts(g, dt);
    for (const p of g.plats) if (p.type === "line") p.x = W / 2 + Math.sin(g.t * p.spd + p.ph) * p.amp;
    const ax = (keys.current.r ? 1 : 0) - (keys.current.l ? 1 : 0); c.vx = ax * MOVE; if (ax) c.face = ax;
    c.x += c.vx * dt; c.x = Math.max(22, Math.min(W - 22, c.x));
    if (c.onPlat) { if (c.onPlat.type === "line") c.x += (Math.sin(g.t * c.onPlat.spd + c.onPlat.ph) - Math.sin((g.t - dt) * c.onPlat.spd + c.onPlat.ph)) * c.onPlat.amp; if (Math.abs(c.x - c.onPlat.x) > c.onPlat.w / 2 + 10) c.onPlat = null; }
    // physics
    if (!c.onPlat) { c.vy += GRAV * dt; c.y += c.vy * dt; }
    if (!c.onPlat && c.vy > 0) {
      for (const p of g.plats) { const half = p.w / 2; if (Math.abs(c.x - p.x) < half + 8 && c.y >= p.y - 16 && c.y - c.vy * dt <= p.y + 8) { c.y = p.y; c.vy = 0; c.onPlat = p; c.jumps = 1; if (p.type === "line") { g.climbing = true; c.floor = Math.max(c.floor, p.floor); g.score = Math.max(g.score, 200 + p.floor * 120); scoreRef.current = g.score; sfx.coin && sfx.coin(); } break; } }
    }
    // reach window
    if (c.y <= g.winY + 40 && Math.abs(c.x - W / 2) < W * 0.22) { winClimb(g); return; }
    // fall to street after climbing
    if (c.onPlat && c.onPlat.type === "ground" && g.climbing) { loseLife(g); return; }
    // camera
    g.camY += (c.y - H * 0.6 - g.camY) * Math.min(1, dt * 6);
    g.camY = Math.min(g.camY, g.worldBot - H); g.camY = Math.max(g.camY, g.winY - H * 0.42);
    setHudFromGame(g);
  };

  const updateHouse = (g, dt) => {
    g.t += dt; const c = g.cat; const { W, H } = dim.current; if (g.grace > 0) g.grace -= dt; stepFarts(g, dt);
    g.timeLeft -= dt; if (g.timeLeft <= 0) { g.timeLeft = 0; loseLife(g); g.timeLeft = Math.max(18, 42 - g.diff * 4); return; }
    const ax = (keys.current.r ? 1 : 0) - (keys.current.l ? 1 : 0), ay = (keys.current.d ? 1 : 0) - (keys.current.u ? 1 : 0);
    c.x += ax * MOVE * dt; c.y += ay * MOVE * dt; if (ax) c.face = ax;
    c.x = Math.max(20, Math.min(W - 20, c.x)); c.y = Math.max(H * 0.42, Math.min(H - 24, c.y));
    for (const k of g.kibble) if (!k.got && Math.hypot(c.x - k.x, c.y - k.y) < 26) { k.got = true; g.eaten++; g.score += 40; scoreRef.current = g.score; sfx.coin && sfx.coin(); }
    if (g.eaten >= g.total) { winHouse(g); return; }
    // bulldog
    const d = g.dog; const dist = Math.hypot(c.x - d.x, c.y - d.y);
    if (d.stun > 0) { d.stun -= dt; }
    else if (d.state !== "chase") { if (dist < 150) d.wake = Math.min(100, d.wake + dt * 30); else d.wake = Math.max(0, d.wake - dt * 12); if (d.wake >= 100) { d.state = "chase"; sfx.wrong && sfx.wrong(); } }
    else { const a = Math.atan2(c.y - d.y, c.x - d.x); const sp = 140 + g.diff * 12; d.x += Math.cos(a) * sp * dt; d.y += Math.sin(a) * sp * dt; if (dist < 30 && g.grace <= 0) { loseLife(g); return; } }
    setHudFromGame(g);
  };

  // ================= render =================
  const drawImg = (ctx, key, x, y, w, h, flip) => {
    const im = imgs.current[key];
    if (im && im.complete && im.naturalWidth) { ctx.save(); ctx.translate(x, y); if (flip < 0) ctx.scale(-1, 1); ctx.drawImage(im, -w / 2, -h / 2, w, h); ctx.restore(); }
    else { ctx.fillStyle = "rgba(255,255,255,0.15)"; ctx.fillRect(x - w / 2, y - h / 2, w, h); }
  };

  const render = () => {
    const cv = canvasRef.current; if (!cv) return; const ctx = cv.getContext("2d");
    const { W, H, dpr } = dim.current; const g = G.current;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    if (!g) { ctx.fillStyle = "#0b1020"; ctx.fillRect(0, 0, W, H); return; }
    if (g.stage === 1) renderClimb(ctx, g, W, H); else renderHouse(ctx, g, W, H);
  };

  const renderClimb = (ctx, g, W, H) => {
    ctx.fillStyle = "#0a0f1f"; ctx.fillRect(0, 0, W, H);
    const toY = (wy) => wy - g.camY;
    const bTop = toY(g.worldTop), bH = g.worldBot - g.worldTop;
    const bg = imgs.current.bgAlley;
    if (bg && bg.complete && bg.naturalWidth) ctx.drawImage(bg, 0, bTop, W, bH);
    // open window (goal)
    drawImg(ctx, "window", W / 2, toY(g.winY), 118, 118, 1);
    ctx.fillStyle = "#fff"; ctx.font = "bold 12px system-ui"; ctx.textAlign = "center"; ctx.fillText("DOEL 🪟", W / 2, toY(g.winY) - 66);
    // clotheslines
    for (const p of g.plats) {
      if (p.type !== "line") continue; const y = toY(p.y); if (y < -40 || y > H + 40) continue;
      const l = p.x - p.w / 2, r = p.x + p.w / 2;
      ctx.strokeStyle = "rgba(20,20,20,0.85)"; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(l - 12, y - 3); ctx.lineTo(r + 12, y - 3); ctx.stroke();
      const cols = ["#e0405b", "#2f7ff0", "#f4c40f", "#2bb673", "#f5871f", "#ffffff"]; const n = 4;
      for (let i = 0; i < n; i++) { const cx = l + ((i + 0.5) / n) * p.w; ctx.fillStyle = cols[(p.floor + i) % cols.length]; ctx.fillRect(cx - 12, y - 2, 24, 22); ctx.fillStyle = "rgba(0,0,0,0.15)"; ctx.fillRect(cx - 12, y - 2, 24, 4); }
      ctx.fillStyle = "rgba(255,255,255,0.9)"; ctx.font = "bold 11px system-ui"; ctx.fillText(String(p.floor), r + 22, y + 2);
    }
    // farts
    for (const f of g.farts) { ctx.globalAlpha = Math.max(0, 1 - f.t / 0.6); drawImg(ctx, "fart", f.x, toY(f.y) + f.t * 40, 54, 54, 1); ctx.globalAlpha = 1; }
    // cat
    const c = g.cat; const falling = !c.onPlat && c.vy > 240;
    if (!(g.grace > 0 && Math.floor(g.t * 12) % 2)) drawImg(ctx, falling ? "catScared" : "catJump", c.x, toY(c.y) - 26, 74, 74, c.face);
  };

  const renderHouse = (ctx, g, W, H) => {
    const bg = imgs.current.bgHouse;
    if (bg && bg.complete && bg.naturalWidth) { const s = Math.max(W / bg.naturalWidth, H / bg.naturalHeight); const dw = bg.naturalWidth * s, dh = bg.naturalHeight * s; ctx.drawImage(bg, (W - dw) / 2, (H - dh) / 2, dw, dh); }
    else { ctx.fillStyle = "#3a2418"; ctx.fillRect(0, 0, W, H); }
    ctx.fillStyle = "rgba(0,0,0,0.28)"; ctx.fillRect(0, 0, W, H * 0.42);
    for (const k of g.kibble) if (!k.got) drawImg(ctx, "kibble", k.x, k.y, 34, 34, 1);
    // bulldog
    const d = g.dog; const st = d.stun > 0 ? "sleep" : d.state === "chase" ? "angry" : d.wake > 40 ? "angry" : "sleep";
    drawImg(ctx, st === "angry" ? "bulldogAngry" : "bulldogSleep", d.x, d.y, 96, 96, d.state === "chase" && g.cat.x < d.x ? -1 : 1);
    if (st !== "angry") { ctx.fillStyle = "#fff"; ctx.font = "bold 16px system-ui"; ctx.textAlign = "center"; ctx.fillText("z".repeat(1 + Math.floor(g.t) % 3), d.x + 42, d.y - 40); }
    else if (d.state !== "chase") { ctx.fillStyle = "#ffd84a"; ctx.font = "bold 20px system-ui"; ctx.textAlign = "center"; ctx.fillText("❓", d.x, d.y - 52); }
    // farts
    for (const f of g.farts) { ctx.globalAlpha = Math.max(0, 1 - f.t / 0.6); drawImg(ctx, "fart", f.x, f.y, 60, 60, 1); ctx.globalAlpha = 1; }
    const c = g.cat; if (!(g.grace > 0 && Math.floor(g.t * 12) % 2)) drawImg(ctx, "catJump", c.x, c.y, 64, 64, c.face);
  };

  const openBoard = async () => { setPhase("board"); try { const { data } = await api.get("/child/alley-leaderboard"); setBoard(data); } catch { } };
  const muted = { color: "var(--t-muted)" };
  const padBtn = "flex items-center justify-center rounded-2xl text-white/90 active:scale-95 transition-transform";
  const padStyle = { background: "rgba(255,255,255,0.16)", backdropFilter: "blur(2px)" };

  if (phase === "menu")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-4">
            <Button variant="ghost" onClick={() => navigate("/leerling/speelhoek")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="alley-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Catz Fart Attack! 💨🐱</h1>
          </div>
          <img src={A.logo} alt="Catz Fart Attack" className="w-full max-w-[300px] mx-auto mb-3 rounded-3xl shadow-lg" style={{ border: "3px solid var(--t-accent)" }} data-testid="alley-logo" />
          <p className="font-semibold mb-5 text-center" style={muted}>Klim langs de <b>waslijnen</b> naar het open raam op verdieping 8. Mis je een sprong? Druk snel op <b>SCHEET 💨</b> om jezelf omhoog te scheten! Eet daarna alle <b>brokjes</b> in huis — maar wek de <b>bulldog</b> niet! 🐶</p>
          <Button onClick={startGame} data-testid="alley-start-btn" className="w-full py-6 rounded-2xl font-bold text-lg border-0 mb-3" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>Start spel</Button>
          <Button onClick={openBoard} data-testid="alley-board-btn" className="w-full h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Trophy className="w-4 h-4 mr-1.5" style={{ color: "var(--t-accent)" }} /> Topscores</Button>
          <button onClick={() => setShowHelp(true)} data-testid="alley-help-btn" className="w-full mt-3 flex items-center justify-center gap-1.5 text-sm font-bold" style={muted}><HelpCircle className="w-4 h-4" /> Hoe speel je?</button>
        </div>
        <AnimatePresence>
          {showHelp && (
            <motion.div key="help" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={closeHelp} className="fixed inset-0 z-30 flex items-center justify-center p-5" style={{ background: "rgba(0,0,0,0.6)" }} data-testid="alley-help-modal">
              <motion.div onClick={(e) => e.stopPropagation()} initial={{ scale: 0.8, y: 20 }} animate={{ scale: 1, y: 0 }} exit={{ scale: 0.85, opacity: 0 }} transition={{ type: "spring", stiffness: 280, damping: 20 }} className="w-full max-w-sm rounded-[1.8rem] p-6" style={{ background: "var(--t-surface)", border: "3px solid var(--t-accent)" }}>
                <h2 className="font-heading text-2xl font-black themed-heading text-center mb-4">Zo speel je 🐱</h2>
                <div className="space-y-3">
                  {[["🧗", "Level 1: klim via de waslijnen omhoog. Spring met de knop van lijn naar lijn."],
                    ["💨", "Mis je een sprong of val je? Druk snel op SCHEET om jezelf omhoog te scheten! (3 scheten, laden vanzelf op)"],
                    ["🪟", "Bereik het open raam op verdieping 8 om binnen te komen."],
                    ["🍖", "Level 2: eet ALLE brokjes in huis vóórdat de tijd op is."],
                    ["🐶", "Kom niet te dicht bij de slapende bulldog — anders wordt hij wakker en jaagt hij je op!"],
                    ["💨", "In huis: scheet de bulldog terug om weer te ontsnappen. Haal alles voor een extra leven!"]].map(([e, t], i) => (
                    <div key={i} className="flex items-start gap-3"><span className="text-2xl shrink-0 leading-none">{e}</span><p className="text-sm font-semibold" style={{ color: "var(--t-text)" }}>{t}</p></div>
                  ))}
                </div>
                <Button onClick={closeHelp} data-testid="alley-help-close" className="w-full mt-6 h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}>Aan de slag! 🐱</Button>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>
      </ThemedShell>
    );

  if (phase === "board")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-8 pb-16">
          <div className="flex items-center gap-2 mb-6">
            <Button variant="ghost" onClick={() => setPhase("menu")} className="rounded-full px-3" style={{ color: "var(--t-text)" }} data-testid="alley-board-back"><ArrowLeft className="w-5 h-5" /></Button>
            <h1 className="font-heading text-2xl font-black themed-heading">Topscores 🏆</h1>
          </div>
          <ThemedCard className="p-4" data-testid="alley-board">
            {!board ? <p className="text-center py-6" style={muted}>Laden…</p> : board.top.length === 0 ? <p className="text-center py-6" style={muted}>Nog geen scores. Wees de eerste!</p> :
              board.top.map((r, i) => (
                <div key={i} className="flex items-center gap-3 px-2 py-2.5 rounded-xl" style={{ background: r.me ? "var(--t-surface2)" : "transparent" }}>
                  <span className="w-7 font-heading font-black text-lg" style={{ color: i === 0 ? "#f4c40f" : i === 1 ? "#b8c0cc" : i === 2 ? "#d08b50" : "var(--t-muted)" }}>{i + 1}</span>
                  <Medal className="w-4 h-4" style={{ color: i < 3 ? "var(--t-accent)" : "var(--t-muted)" }} />
                  <span className="flex-1 font-bold">{r.name}{r.me ? " (jij)" : ""}</span>
                  <span className="font-heading font-black">{r.best}</span>
                </div>
              ))}
          </ThemedCard>
        </div>
      </ThemedShell>
    );

  if (phase === "over")
    return (
      <ThemedShell>
        <div className="max-w-md mx-auto px-5 pt-14 pb-16 text-center">
          <div className="text-6xl mb-2">😿</div>
          <h1 className="font-heading text-3xl font-black themed-heading">Game over</h1>
          <p className="font-heading text-4xl font-black mt-3 themed-heading" data-testid="alley-final-score">{result?.score ?? scoreRef.current}</p>
          {result && result.xp_earned != null && (
            <div className="flex justify-center gap-4 mt-4">
              <span className="font-heading font-extrabold" style={{ color: "var(--t-accent)" }}>+{result.xp_earned} XP</span>
              <span className="font-heading font-extrabold" style={{ color: "var(--t-primary)" }}>+{result.coins_earned} 🪙</span>
            </div>
          )}
          <div className="flex flex-col gap-2 mt-7">
            <Button onClick={startGame} data-testid="alley-again-btn" className="h-12 rounded-xl font-bold border-0" style={{ background: "var(--t-primary)", color: "var(--t-on-primary)" }}><RotateCcw className="w-4 h-4 mr-1.5" /> Opnieuw</Button>
            <Button onClick={openBoard} className="h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Trophy className="w-4 h-4 mr-1.5" style={{ color: "var(--t-accent)" }} /> Topscores</Button>
            <Button onClick={() => navigate("/leerling/speelhoek")} className="h-12 rounded-xl font-bold border-2 bg-transparent" style={{ borderColor: "var(--t-border)", color: "var(--t-text)" }}><Home className="w-4 h-4 mr-1.5" /> Speelhoek</Button>
          </div>
        </div>
      </ThemedShell>
    );

  const isHouse = hud.stage === 2;
  return (
    <div className="fixed inset-0 overflow-hidden select-none" style={{ background: "#0b1020" }} data-testid="alley-stage">
      <div className="absolute top-0 inset-x-0 z-10 flex items-center justify-between px-4 py-2 text-white font-heading font-black text-sm" style={{ background: "rgba(0,0,0,0.5)" }}>
        <button onClick={() => { cancelAnimationFrame(rafRef.current); music.stop(); setPhase("menu"); }} data-testid="alley-stage-back"><ArrowLeft className="w-6 h-6" /></button>
        <span data-testid="alley-hud-info1">{hud.info1}</span>
        <span data-testid="alley-hud-fart">💨 {hud.fart}</span>
        <span data-testid="alley-hud-lives">{"❤️".repeat(Math.max(0, hud.lives))}</span>
        <span data-testid="alley-hud-score">{hud.score}</span>
      </div>
      <canvas ref={canvasRef} className="absolute inset-0 w-full h-full touch-none" data-testid="alley-canvas" />

      <AnimatePresence>
        {banner && (
          <motion.div key="b" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="absolute inset-0 z-20 flex items-center justify-center" style={{ background: "rgba(0,0,0,0.55)" }} data-testid="alley-banner">
            <motion.div initial={{ scale: 0.6, y: 20 }} animate={{ scale: 1, y: 0 }} transition={{ type: "spring", stiffness: 280, damping: 16 }} className="text-center px-9 py-8 rounded-[2rem]" style={{ background: "#1c1206", border: "3px solid #f4c40f" }}>
              <div className="text-5xl mb-1">🎉</div>
              <h2 className="font-heading text-3xl font-black" style={{ color: "#fff" }}>{banner.title}</h2>
              <p className="font-semibold mt-2 text-sm" style={{ color: "rgba(255,255,255,0.75)" }}>{banner.sub}</p>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="absolute bottom-5 inset-x-0 z-10 flex items-end justify-between px-5 pointer-events-none">
        {isHouse ? (
          <div className="grid grid-cols-3 gap-2 w-44" style={{ pointerEvents: "auto" }}>
            <span /><button onPointerDown={() => (keys.current.u = true)} onPointerUp={() => (keys.current.u = false)} onPointerLeave={() => (keys.current.u = false)} data-testid="alley-up" className={`${padBtn} aspect-square`} style={padStyle}><ArrowUp className="w-8 h-8" strokeWidth={2.5} /></button><span />
            <button onPointerDown={() => (keys.current.l = true)} onPointerUp={() => (keys.current.l = false)} onPointerLeave={() => (keys.current.l = false)} data-testid="alley-left" className={`${padBtn} aspect-square`} style={padStyle}><ArrowL className="w-8 h-8" strokeWidth={2.5} /></button>
            <button onPointerDown={() => (keys.current.d = true)} onPointerUp={() => (keys.current.d = false)} onPointerLeave={() => (keys.current.d = false)} data-testid="alley-down" className={`${padBtn} aspect-square`} style={padStyle}><ArrowDown className="w-8 h-8" strokeWidth={2.5} /></button>
            <button onPointerDown={() => (keys.current.r = true)} onPointerUp={() => (keys.current.r = false)} onPointerLeave={() => (keys.current.r = false)} data-testid="alley-right" className={`${padBtn} aspect-square`} style={padStyle}><ArrowRight className="w-8 h-8" strokeWidth={2.5} /></button>
          </div>
        ) : (
          <div className="flex gap-3" style={{ pointerEvents: "auto" }}>
            <button onPointerDown={() => (keys.current.l = true)} onPointerUp={() => (keys.current.l = false)} onPointerLeave={() => (keys.current.l = false)} data-testid="alley-left" className={`${padBtn} w-16 h-16`} style={padStyle}><ArrowL className="w-8 h-8" strokeWidth={2.5} /></button>
            <button onPointerDown={() => (keys.current.r = true)} onPointerUp={() => (keys.current.r = false)} onPointerLeave={() => (keys.current.r = false)} data-testid="alley-right" className={`${padBtn} w-16 h-16`} style={padStyle}><ArrowRight className="w-8 h-8" strokeWidth={2.5} /></button>
          </div>
        )}
        <div className="flex gap-3" style={{ pointerEvents: "auto" }}>
          <button onPointerDown={doFart} data-testid="alley-fart" className="flex flex-col items-center justify-center w-20 h-20 rounded-full font-heading font-black text-white active:scale-95 transition-transform" style={{ background: "#3fae3f", boxShadow: "0 6px 0 #2b7a2b" }}><span className="text-2xl leading-none">💨</span><span className="text-[11px] mt-0.5">SCHEET</span></button>
          {!isHouse && <button onPointerDown={doJump} data-testid="alley-jump" className="flex items-center justify-center w-20 h-20 rounded-full font-heading font-black text-white active:scale-95 transition-transform" style={{ background: "#e0a020", boxShadow: "0 6px 0 #a8760f" }}><ArrowUp className="w-9 h-9" /></button>}
        </div>
      </div>
    </div>
  );
}
